import React, { useContext } from 'react';
import { RedeemContext } from './Context';
import './pre-book.scss';
import { PRE_BOOK_STATUS } from '../../constants/app-constants';

const CalendarIcon = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <rect x="7" y="11" width="34" height="30" rx="3" />
    <path d="M15 5v12M33 5v12M7 20h34" />
    <path d="M16 27h5v5h-5zM27 27h5" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <circle cx="24" cy="24" r="19" />
    <path d="M24 12v13l9 6" />
  </svg>
);

const UtensilsIcon = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <path d="M10 5v13M17 5v13M10 12h7M13.5 18v24" />
    <path d="M30 5v13c0 5 8 5 8 0V5M34 23v19" />
  </svg>
);

const getProductImage = product => {
  const image = product.image_urls
    ?.find(item => item.category === 'plp')
    ?.images?.find(item => item.name === 'desktop')?.image;

  return image
    ? {
        src: image['2x'] || image['1x'],
        srcSet: [
          image['1x'] && `${image['1x']} 1x`,
          image['2x'] && `${image['2x']} 2x`,
          image['3x'] && `${image['3x']} 3x`
        ]
          .filter(Boolean)
          .join(', ')
      }
    : null;
};

const OfferDetail = ({ icon: Icon, title, value }) => (
  <div className="doomsday-banner__detail">
    <span className="doomsday-banner__detail-icon">
      <Icon />
    </span>

    <div className="doomsday-banner__detail-copy">
      <span>{title}</span>
      <span>{value}</span>
    </div>
  </div>
);

const getNames = (status, canRedeem, description) => {
  const names = {
    heading: 'Doomsday is here',
    buttonName: status,
    description
  };
  if (canRedeem)
    return {
      ...names,
      buttonName: 'REDEEM',
      description: `${description} awaits for you!`
    };

  if (status === PRE_BOOK_STATUS.RESERVED)
    return {
      ...names,
      heading: 'Your meal is reserved',
      description: `${description} awaits for you!`
    };

  if (status === PRE_BOOK_STATUS.REDEEMED) {
    return {
      ...names,
      heading: 'Your meal was redeemed',
      buttonName: 'VIEW REDEEMED ORDER'
    };
  }

  if (status === PRE_BOOK_STATUS.EXPIRED) {
    return {
      ...names,
      heading: 'Redemption window ended'
    };
  }

  return names;
};

const DoomsdayCard = ({
  product,
  backgroundImage,
  redeemUntil,
  redeemableText = 'Dine-in only',
  availableAt = 'The Restaurant',
  onRedeem
}) => {
  const { selectedItems, status } = useContext(RedeemContext);
  const image = getProductImage(product);

  const canRedeem =
    product?.can_redeem === true && product?.prebook_status === 'RESERVED';

  const isSelectedSelected = selectedItems.includes(product?.prebook_code);

  const isButtonDisable = () => {
    if (product?.can_redeem) return false;
    return [PRE_BOOK_STATUS.RESERVED, PRE_BOOK_STATUS.EXPIRED].includes(
      product?.prebook_status
    );
  };

  const names = getNames(
    product?.prebook_status,
    canRedeem,
    product?.product_name
  );

  return (
    <article
      className="doomsday-banner"
      data-selected={isSelectedSelected}
      style={{
        '--doomsday-background': backgroundImage
          ? `url("${backgroundImage}")`
          : image?.src
          ? `url("${image.src}")`
          : 'none'
      }}
    >
      <div className="doomsday-banner__main">
        <div className="doomsday-banner__heading">
          <span className="doomsday-banner__code">{product?.prebook_code}</span>

          <h2 className="doomsday-banner__title">{names?.heading}</h2>

          <p className="doomsday-banner__subtitle">{names.description}</p>
        </div>

        <div className="doomsday-banner__details">
          <OfferDetail
            icon={CalendarIcon}
            title="Valid until"
            value={redeemUntil}
          />

          <OfferDetail
            icon={ClockIcon}
            title="Redeemable"
            value={redeemableText}
          />

          <OfferDetail
            icon={UtensilsIcon}
            title="Available at"
            value={availableAt}
          />
        </div>
      </div>

      <div className="doomsday-banner__footer">
        <span className="doomsday-banner__watermark" aria-hidden="true">
          ✦
        </span>
        {product?.amount && (
          <span className="amount">₹ {(product?.amount || 0).toFixed(2)}</span>
        )}

        {[PRE_BOOK_STATUS.RESERVED, PRE_BOOK_STATUS.EXPIRED].includes(
          status
        ) && (
          <button
            type="button"
            className="doomsday-banner__redeem"
            disabled={isButtonDisable()}
            onClick={() => onRedeem?.(product)}
          >
            {names.buttonName}
          </button>
        )}
      </div>
    </article>
  );
};

const DoomsdayReservationList = ({
  products = [],
  backgroundImage,
  redeemUntil,
  redeemableText = 'Dine-in only',
  availableAt = 'The Restaurant',
  onRedeem
}) => {
  if (!products.length) {
    return (
      <p className="doomsday-banner__empty">No pre-booked products found.</p>
    );
  }

  return (
    <section
      className="doomsday-reservations"
      aria-label="Pre-booked Doomsday meals"
    >
      {products.map(product => (
        <DoomsdayCard
          key={product.product_entity_id}
          product={product}
          backgroundImage={backgroundImage}
          redeemUntil={redeemUntil}
          redeemableText={redeemableText}
          availableAt={availableAt}
          onRedeem={onRedeem}
        />
      ))}
    </section>
  );
};

export default DoomsdayReservationList;
